import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import GameCard, { type CardReveal } from './GameCard'
import ChipRow from './ChipRow'
import ChipInfoModal from './ChipInfoModal'
import { chipDefinitions } from '../mockChips'
import { computeGoalfestBonus, isCleanSweepCleared } from '../season'
import type { SimGameStatus } from '../gameweekSimulation'
import { hasNoPredictions } from '../predictionCycle'
import type {
  ChipId,
  Game,
  GameweekResult,
  Prediction,
  QuestId,
  QuestPickState,
} from '../types'
import { questPointsForGameweek, type QuestResult } from '../questResults'
import { getQuestsForGameweek } from '../quests'
import GameweekGridHeader from './gameweekGrid/GameweekGridHeader'
import GameweekQuests from './gameweekGrid/GameweekQuests'
import GameweekDevTools from './gameweekGrid/GameweekDevTools'
import RevealEffectsLayer from './gameweekGrid/RevealEffectsLayer'
import BankerPrompts from './gameweekGrid/BankerPrompts'
import StartCtaCard from './gameweekGrid/StartCtaCard'
import EntryDock from './gameweekGrid/EntryDock'
import {
  computeGameweekPointsSoFar,
  computeRevealedResults,
  revealCategoryFor,
} from './gameweekGrid/gameweekPoints'
import { useRevealCascade } from './gameweekGrid/useRevealCascade'
import {
  CHIP_MULTIPLIER_DESCRIPTIONS,
  CHIP_PLAY_CONFIRMATIONS,
  useGameweekChips,
} from './gameweekGrid/useGameweekChips'
import { useGwPointsHero } from './gameweekGrid/useGwPointsHero'
import { usePredictionEntrance } from './gameweekGrid/usePredictionEntrance'
import { useEntryIgnite } from './gameweekGrid/useEntryIgnite'
import { buildEntryDock } from './gameweekGrid/entryDockState'
import { useDevToolsRail } from './gameweekGrid/useDevToolsRail'
import { resolveLiveStates } from './gameweekGrid/liveState'
import { questRevealGates } from './gameweekGrid/questReveal'
import { useMatchClock } from '../app/matchClock'
import type { LiveFixture } from '../api/football'
import { EMPTY_GAMEWEEK_STATS } from '../app/useGameweekStats'
import type { GameweekStats } from '../storage'
import type { DataMode } from '../dataMode'
import './GameweekGrid.css'

interface GameweekGridProps {
  gameweek: number
  games: Game[]
  predictions: Record<string, Prediction>
  actualResults: Record<string, { homeScore: number; awayScore: number }>
  onSelectGame: (gameId: string) => void
  onStartCycle: () => void
  /** Enter that same walk partway, at one fixture — the entry dock's primary button. */
  onStartCycleAt?: (gameId: string) => void
  /** True while the start CTA's walk through every fixture is open. The entry dock
   *  stays out of the way until the player leaves it. */
  isCycling?: boolean
  history: GameweekResult[]
  doubleUpUsedGameweeks: number[]
  onPlayDoubleUp: () => void
  onCancelDoubleUp: () => void
  cleanSweepUsedGameweeks: number[]
  onPlayCleanSweep: () => void
  onCancelCleanSweep: () => void
  goalfestUsedGameweeks: number[]
  onPlayGoalfest: () => void
  onCancelGoalfest: () => void
  // Frozen final Goalfest bonus for each gameweek it was played and has since
  // been finalized — keyed by gameweek number.
  goalfestFinalBonusByGameweek: Record<number, number>
  // Frozen final Clean Sweep total (0 if the sweep was missed) for each gameweek it was played
  // and has since been finalized — keyed by gameweek number.
  cleanSweepFinalTotalByGameweek: Record<number, number>
  deadlinePassed: boolean
  /** The live poll's server-clock offset. Feeds the header countdown, so it
   *  counts down to the deadline the server enforces rather than to this device's idea of it. */
  serverOffsetMs: number
  bankerGameId: string | null
  onSimulateDeadline: () => void
  resultsRevealed: boolean
  onToggleResults: () => void
  /** Open a past gameweek's results modal. `source` carries the tapped picker row's
   *  name + points block plus the clone to fly in its place, so the modal can be entered through
   *  the shared-element expand; callers that don't animate can ignore it. */
  onSelectHistoryGameweek: (
    gameweek: number,
    source?: { anchorEl: HTMLElement | null; clone: ReactNode }
  ) => void
  /** Touch-down feedback for a picker row — the light-impact haptic, fired on press. */
  onHistoryPressFeedback?: () => void
  /** True from the moment a picker row is tapped until the results modal is gone again: the
   *  dropdown fades out as the row lifts and fades back in, still open, on return. */
  historyTransitionActive?: boolean
  onFinalizeGameweek: () => void
  onSimulateSeason: () => void
  onDevChangeGameweek: (delta: number) => void
  onResetAll: (confirm?: string) => void
  /** Whether the signed-in user is the admin. Only the admin
   * sees the dev-tools rail; non-admins get no handle, rail, or controls. */
  isAdmin: boolean
  /** Which world the server is running in — decides which dev
   * controls exist. Null until `/me` lands, which shows no rail. */
  dataMode: DataMode | null
  /** In-play fixtures from the last `/api/live` poll. This is the shared truth —
   *  every user watching the same match sees the same snapshot — and it is what drives the cards'
   *  live state wherever it has an opinion. Empty in mock mode, where `simGameStatuses` below is
   *  the only source. */
  liveFixtures?: LiveFixture[]
  /** The `serverTime` that snapshot was taken at, anchoring the ticking match minute (§5). */
  liveServerTime?: string | null
  simGameStatuses?: Record<string, SimGameStatus>
  simLiveResults?: Record<string, { homeScore: number; awayScore: number }>
  isSimulatingGameweek?: boolean
  onSimulateGameweekRealtime?: () => void
  /** GameIds whose full-time reveal has already played for this user. `null` while still loading —
   * the reveal holds until it resolves so a
   * once-ever Phase A can't fire against a stale/empty set. */
  revealSeenGameIds: Set<string> | null
  onMarkRevealSeen: (gameIds: string[]) => void
  onDevClearRevealSeen: () => void
  /** Whether the user has started this gameweek — tapped the
   * "Predict Now" hero at least once. Gates the Weekly Quests section's
   * visibility (hidden vs. shown); `allPredicted` below then drives its
   * dimmed-vs-unlocked look once shown. */
  hasStartedGw: boolean
  /** This gameweek's weekly quest picks, keyed by the 2 quest
   * ids the round-robin schedule surfaced — see quests.ts. */
  questPicks: Partial<Record<QuestId, QuestPickState>>
  /** This gameweek's graded quest truth. Absent quests are ungraded, not
   * zero-scoring — their cards stay sealed. Defaulted so the many call sites that render a
   * gameweek without quests (tests, history) need not pass it. */
  questResults?: Partial<Record<QuestId, QuestResult>>
  /** Which quests this deployment runs, from /me. Empty means
   * the Weekly Quests section renders nowhere. */
  questsEnabled: QuestId[]
  onPickQuestTeam: (questId: QuestId, team: string) => void
  onPickQuestMatch: (questId: QuestId, game: Game) => void
  onSelectQuestSide: (questId: QuestId, side: 'over' | 'under') => void
  /** Open a fixture on the Fixtures page. Passed straight to each live/settled card,
   *  which is the only grid where that destination exists. */
  onOpenFixture?: (gameId: string) => void
  /** The gameweek's real average and highest, from the server. Defaulted for the many call sites
   *  (tests, history) that render a grid without them — the tiles read 0, as they do before any
   *  result lands. */
  gameweekStats?: GameweekStats
  /** The signed-in user's id, so the Highest tile can tell when it is pointing at them. */
  myUserId?: string
  /** Press Highest to open that player's profile on their "GW N Calls" sheet. */
  onOpenTopScorer?: (userId: string, displayName: string) => void
  /** The first gameweek this account may play, from `/me`. Any gameweek before it closed before
   *  the account existed: the server writes them no predictions and no score, so the grid shows
   *  the fixtures with nothing attached and says where their season starts. Null for everyone who
   *  was here from the season's first deadline. */
  firstPlayableGameweek?: number | null
}

function GameweekGrid({
  gameweek,
  games,
  predictions,
  actualResults,
  onSelectGame,
  onStartCycle,
  onStartCycleAt,
  isCycling = false,
  history,
  doubleUpUsedGameweeks,
  onPlayDoubleUp,
  onCancelDoubleUp,
  cleanSweepUsedGameweeks,
  onPlayCleanSweep,
  onCancelCleanSweep,
  goalfestUsedGameweeks,
  onPlayGoalfest,
  onCancelGoalfest,
  goalfestFinalBonusByGameweek,
  cleanSweepFinalTotalByGameweek,
  deadlinePassed,
  serverOffsetMs,
  bankerGameId,
  onSimulateDeadline,
  resultsRevealed,
  onToggleResults,
  onSelectHistoryGameweek,
  onHistoryPressFeedback,
  historyTransitionActive = false,
  onFinalizeGameweek,
  onSimulateSeason,
  onDevChangeGameweek,
  onResetAll,
  isAdmin,
  dataMode,
  liveFixtures,
  liveServerTime = null,
  simGameStatuses,
  simLiveResults,
  isSimulatingGameweek = false,
  onSimulateGameweekRealtime,
  revealSeenGameIds,
  onMarkRevealSeen,
  onDevClearRevealSeen,
  hasStartedGw,
  questPicks,
  questResults = {},
  questsEnabled,
  onPickQuestTeam,
  onPickQuestMatch,
  onSelectQuestSide,
  onOpenFixture,
  gameweekStats = EMPTY_GAMEWEEK_STATS,
  myUserId = '',
  onOpenTopScorer,
  firstPlayableGameweek = null,
}: GameweekGridProps) {
  const [openChipId, setOpenChipId] = useState<ChipId | null>(null)
  const { devToolsOpen, setDevToolsOpen } = useDevToolsRail()

  // One live state per fixture, resolved here rather than per card — see
  // liveState.ts for which source wins.
  const liveStateById = useMemo(
    () => resolveLiveStates({ games, liveFixtures, simGameStatuses, simLiveResults }),
    [games, liveFixtures, simGameStatuses, simLiveResults]
  )
  // ...and one clock for all of them (§5): a 10-fixture gameweek must not run 10 intervals. Passing
  // `null` while nothing the server reported is in play means no interval runs at all — the mock
  // sim has no minute to tick, and neither has a quiet Tuesday.
  const hasServerLive = Object.values(liveStateById).some((state) => state.status !== null)
  const { elapsedMs: liveElapsedMs } = useMatchClock(hasServerLive ? liveServerTime : null)

  // The week closed before this account existed. Not the same as "hasn't picked yet": there is
  // nothing to pick, so the start CTA and the empty-grid framing both have to stand down.
  const joinedLate = firstPlayableGameweek !== null && gameweek < firstPlayableGameweek

  const predictedCount = games.filter((g) => predictions[g.id]).length
  const noPredictionsYet = hasNoPredictions(games, predictions)
  const allPredicted = games.length > 0 && predictedCount === games.length

  const { enteringGameIds } = usePredictionEntrance(
    gameweek,
    games,
    predictions
  )
  // The header count is derived from the entering set rather than kept as its own
  // synced state, so it lags behind the live count by exactly the set of
  // still-animating ids, with no extra state to drift.
  const displayedPredictedCount = predictedCount - enteringGameIds.size

  const chips = useGameweekChips({
    gameweek,
    deadlinePassed,
    doubleUpUsedGameweeks,
    cleanSweepUsedGameweeks,
    goalfestUsedGameweeks,
    goalfestFinalBonusByGameweek,
    cleanSweepFinalTotalByGameweek,
  })
  const { isDoubleUpActive, isCleanSweepActive, isGoalfestActive } = chips
  const openChip = chipDefinitions.find((c) => c.id === openChipId)
  const openChipConfig = openChipId ? chips.chipConfigs[openChipId] : undefined

  const isGameRevealed = (gameId: string) =>
    resultsRevealed || simGameStatuses?.[gameId] === 'final'
  const allFixturesResolved = games.length > 0 && games.every((game) => isGameRevealed(game.id))

  const revealedResultsMap = computeRevealedResults(games, actualResults, isGameRevealed)
  // Quest points are part of the gameweek total, not a score of their own (the // decision,
  // implemented): +2 per correct quest, so at most +4 on top of the prediction points. They land
  // when the quests are graded rather than as the fixtures settle — which is also how the server
  // folds them into `season_history`, so the hero and the league table agree.
  const questsThisGw = getQuestsForGameweek(gameweek, questsEnabled)
  const questPointsEarned = questPointsForGameweek(questsThisGw, questResults, questPicks)
  const gameweekPointsSoFar =
    computeGameweekPointsSoFar({
      games,
      predictions,
      revealedResults: revealedResultsMap,
      bankerGameId,
      isDoubleUpActive,
      isGoalfestActive,
      isCleanSweepActive,
    }) + questPointsEarned

  // Live Goalfest ticker — reuses the same
  // revealed-results view the running total above derives from, so it's
  // always consistent with `gameweekPointsSoFar`'s Goalfest contribution.
  const goalfestLiveBonus = computeGoalfestBonus(games, predictions, revealedResultsMap)

  // Live Clean Sweep "cleared so far" state — flips to
  // false (and gameweekPointsSoFar to 0, via computeCleanSweepPoints) the
  // moment any revealed fixture's outcome is wrong, even before the rest of
  // the gameweek finishes.
  const cleanSweepClearedSoFar = isCleanSweepActive
    ? isCleanSweepCleared(games, predictions, revealedResultsMap)
    : false

  const hero = useGwPointsHero({
    games,
    predictions,
    actualResults,
    isGameRevealed,
    resultsRevealed,
    simGameStatuses,
    gameweekPointsSoFar,
    goalfestLiveBonus,
    allFixturesResolved,
    stats: gameweekStats,
    myUserId,
  })

  const { revealPhaseById, spotlitIds, volleyedIds, bursts, registerCard, revealLayerRef } =
    useRevealCascade({
      games,
      predictions,
      actualResults,
      resultsRevealed,
      simGameStatuses,
      revealSeenGameIds,
      onMarkRevealSeen,
    })

  // Weekly Quests reveal gates. Data settledness
  // ("a result exists") is deliberately not the same condition as reveal timing ("the card's
  // animated cascade has finished playing"): the quest's own hit/miss data is safe to compute the
  // instant the data exists, and only the *showing* of it waits on the cascade, so there is no
  // extra recompute delay once the gate opens.
  const { questsRevealed, questsEarlyRevealed } = questRevealGates({
    gameIds: games.map((g) => g.id),
    hasResult: (id) => !!actualResults[id],
    isRevealed: isGameRevealed,
    seenGameIds: revealSeenGameIds,
    // The conductor deletes a card's key on completion, so "no entry" is the completion signal;
    // ids the user saw in a prior session start with no entry at all and count as complete.
    cascadeDone: (id) => revealPhaseById[id] === undefined,
  })

  // Entry ignite — one flash per row, top to bottom, when the
  // user lands on a gameweek that is fully predicted and still open. A fixture
  // in play or already settled means the gameweek is no longer "in", so the
  // wave sits it out; the hook owns the once-per-entry rules.
  const fixtureInPlay = games.some(
    (game) => liveStateById[game.id] !== undefined || actualResults[game.id] !== undefined
  )
  const fixtureRowCount = Math.ceil(games.length / 2)
  // The quest stack is one more 2-column row of the same wave whenever the
  // section is on screen at all. Whether either card actually lights is the
  // card's own call (only 'confirm' joins), so an unpicked or quests-off
  // gameweek simply has a row where nothing happens — or no row at all.
  const questIgniteRow = questsThisGw.length > 0 && hasStartedGw ? fixtureRowCount : null
  const entryDock = buildEntryDock({
    games,
    predictions,
    deadlinePassed,
    fixtureInPlay,
    isCycling,
    joinedLate,
  })

  const ignitedRows = useEntryIgnite({
    gameweek,
    rowCount: fixtureRowCount + (questIgniteRow === null ? 0 : 1),
    eligible: allPredicted && !deadlinePassed && !fixtureInPlay,
  })

  const header = (
    <GameweekGridHeader
      gameweek={gameweek}
      games={games}
      history={history}
      chipUsage={{ doubleUpUsedGameweeks, cleanSweepUsedGameweeks, goalfestUsedGameweeks }}
      chips={chips}
      hero={hero}
      deadlinePassed={deadlinePassed}
      serverOffsetMs={serverOffsetMs}
      noPredictionsYet={noPredictionsYet}
      allPredicted={allPredicted}
      displayedPredictedCount={displayedPredictedCount}
      allFixturesResolved={allFixturesResolved}
      goalfestLiveBonus={goalfestLiveBonus}
      cleanSweepClearedSoFar={cleanSweepClearedSoFar}
      gameweekPointsSoFar={gameweekPointsSoFar}
      onSelectHistoryGameweek={onSelectHistoryGameweek}
      onHistoryPressFeedback={onHistoryPressFeedback}
      onOpenTopScorer={onOpenTopScorer}
      historyTransitionActive={historyTransitionActive}
    />
  )

  const devTools = (
    <GameweekDevTools
      isAdmin={isAdmin}
      mode={dataMode}
      hasGames={games.length > 0}
      devToolsOpen={devToolsOpen}
      setDevToolsOpen={setDevToolsOpen}
      resultsRevealed={resultsRevealed}
      isSimulatingGameweek={isSimulatingGameweek}
      onSimulateDeadline={onSimulateDeadline}
      onToggleResults={onToggleResults}
      onFinalizeGameweek={onFinalizeGameweek}
      onSimulateSeason={onSimulateSeason}
      onDevClearRevealSeen={onDevClearRevealSeen}
      onSimulateGameweekRealtime={onSimulateGameweekRealtime}
      onDevChangeGameweek={onDevChangeGameweek}
      onResetAll={onResetAll}
    />
  )

  if (games.length === 0) {
    return (
      <div className="gameweek-grid">
        {header}
        <p className="gameweek-grid__empty-note">
          Fixtures for this gameweek haven't been added yet.
        </p>
        {devTools}
      </div>
    )
  }

  return (
    <div
      className={`gameweek-grid${
        noPredictionsYet && !joinedLate ? ' gameweek-grid--start' : ''
      }`}
    >
      {header}

      {!deadlinePassed && !noPredictionsYet && (
        <ChipRow
          chips={chipDefinitions}
          getStatus={chips.getStatus}
          getStatusDetail={chips.getStatusDetail}
          getUsedDetail={chips.getUsedDetail}
          onSelectChip={setOpenChipId}
        />
      )}

      {!joinedLate && (
        <BankerPrompts
          gameweek={gameweek}
          games={games}
          predictions={predictions}
          bankerGameId={bankerGameId}
          deadlinePassed={deadlinePassed}
        />
      )}

      {joinedLate && (
        <p className="gameweek-grid__joined-late">
          You joined mid-week. Your first picks are GW {firstPlayableGameweek}.
        </p>
      )}

      {noPredictionsYet && !joinedLate ? (
        <StartCtaCard
          gameweek={gameweek}
          fixtureCount={games.length}
          onStartCycle={onStartCycle}
        />
      ) : (
        <div className="gameweek-grid__grid">
          {games.map((game, index) => {
            const phase = revealPhaseById[game.id]
            const reveal: CardReveal | undefined = phase
              ? { category: revealCategoryFor(game.id, predictions, actualResults), phase }
              : undefined
            const liveState = liveStateById[game.id]
            return (
              <GameCard
                key={game.id}
                ref={(el) => registerCard(game.id, el)}
                game={game}
                prediction={predictions[game.id]}
                actualResult={
                  isGameRevealed(game.id) ? actualResults[game.id] : undefined
                }
                onClick={() => onSelectGame(game.id)}
                isBanker={game.id === bankerGameId}
                isDoubleUpActive={isDoubleUpActive}
                isCleanSweepActive={isCleanSweepActive}
                cleanSweepCleared={cleanSweepClearedSoFar}
                isLive={liveState !== undefined}
                liveResult={liveState?.result}
                liveStatus={liveState?.status ?? null}
                liveMinute={liveState?.minute ?? null}
                liveElapsedMs={liveElapsedMs}
                isEntering={enteringGameIds.has(game.id)}
                deadlinePassed={deadlinePassed}
                reveal={reveal}
                spotlit={spotlitIds.has(game.id)}
                subdued={volleyedIds.has(game.id)}
                igniteIndex={index}
                // Both cards in a row fire together — the wave steps down the
                // grid a row at a time, not a card at a time.
                igniteFiring={ignitedRows.has(Math.floor(index / 2))}
                onOpenFixture={
                  onOpenFixture ? () => onOpenFixture(game.id) : undefined
                }
              />
            )
          })}

          <RevealEffectsLayer
            ref={revealLayerRef}
            bursts={bursts}
          />
        </div>
      )}

      <GameweekQuests
        gameweek={gameweek}
        games={games}
        questPicks={questPicks}
        questResults={questResults}
        questsEnabled={questsEnabled}
        questsRevealed={questsRevealed}
        questsEarlyRevealed={questsEarlyRevealed}
        hasStartedGw={hasStartedGw}
        allPredicted={allPredicted}
        deadlinePassed={deadlinePassed}
        onPickQuestTeam={onPickQuestTeam}
        onPickQuestMatch={onPickQuestMatch}
        onSelectQuestSide={onSelectQuestSide}
        igniteBaseIndex={games.length}
        igniteFiring={questIgniteRow !== null && ignitedRows.has(questIgniteRow)}
      />

      {entryDock && (
        <EntryDock
          model={entryDock}
          gameweek={gameweek}
          igniteRowCount={fixtureRowCount + (questIgniteRow === null ? 0 : 1)}
          bankerGame={games.find((game) => game.id === bankerGameId) ?? null}
          chipLabel={chips.activeChip?.name ?? null}
          onSelectGame={onSelectGame}
          onStartCycleAt={onStartCycleAt ?? onSelectGame}
        />
      )}

      {devTools}

      {openChip && openChipConfig && (
        <ChipInfoModal
          chip={openChip}
          status={chips.getStatus(openChip.id)}
          usesRemaining={
            openChipConfig.seasonCap - openChipConfig.usedGameweeks.length
          }
          seasonCap={openChipConfig.seasonCap}
          multiplierDescription={
            CHIP_MULTIPLIER_DESCRIPTIONS[openChip.id] ?? ''
          }
          currentGameweek={gameweek}
          secondUseUnlockGameweek={openChipConfig.secondUseUnlockGameweek}
          unlockGameweek={openChipConfig.unlockGameweek}
          blockingChipName={chips.getBlockingChipName(openChip.id)}
          deadlinePassed={deadlinePassed}
          playConfirmation={CHIP_PLAY_CONFIRMATIONS[openChip.id]}
          onPlay={() => {
            if (openChip.id === 'doubleUp') onPlayDoubleUp()
            else if (openChip.id === 'cleanSweep') onPlayCleanSweep()
            else if (openChip.id === 'goalfest') onPlayGoalfest()
            setOpenChipId(null)
          }}
          onCancel={() => {
            if (openChip.id === 'doubleUp') onCancelDoubleUp()
            else if (openChip.id === 'cleanSweep') onCancelCleanSweep()
            else if (openChip.id === 'goalfest') onCancelGoalfest()
            setOpenChipId(null)
          }}
          onClose={() => setOpenChipId(null)}
        />
      )}
    </div>
  )
}

export default GameweekGrid
